import React, { useRef, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Footer } from './Footer';
import { OnboardingTutorial } from '@/components/OnboardingTutorial';

export const DashboardLayout: React.FC = () => {
  const mainRef = useRef<HTMLElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      // If the scrolling element is the document or a div inside it, check scrollTop
      if (target && target.scrollTop !== undefined) {
        if (target.scrollTop > 300) {
          setShowScrollTop(true);
        } else if (target.scrollTop === 0) {
          // Only hide if we actually reached the top of the main scroll container
          if (mainRef.current && mainRef.current.scrollTop === 0) {
            setShowScrollTop(false);
          }
        }
      }
    };
    
    // Use capture phase to catch scroll events from internal scrolling containers
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const scrollToTop = () => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Scroll any internal scrolling containers (like in settings or performance pages)
    const scrollContainers = document.querySelectorAll('.overflow-auto, .overflow-y-auto');
    scrollContainers.forEach(container => {
      if (container !== mainRef.current) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      <OnboardingTutorial />
      <Sidebar isSalaryAdvanceVisible={false} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden relative">
        <Topbar />
        <main ref={mainRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-6 animate-fade-in relative">
          <Outlet />
        </main>
        
        {/* Mobile scroll to top button */}
        {showScrollTop && (
          <button 
            onClick={scrollToTop}
            className="md:hidden fixed bottom-16 right-4 z-50 flex flex-col items-center gap-1 drop-shadow-md"
          >
            <div className="w-10 h-10 rounded-full bg-[#FAA61A] text-white flex items-center justify-center shadow-lg transition-transform active:scale-95">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7" /></svg>
            </div>
            <span className="text-[10px] font-bold text-[#FAA61A]">Back to top</span>
          </button>
        )}
        
        <Footer />
      </div>
    </div>
  );
};
export default DashboardLayout;
